<template>
    <div ref="root" class="language-selector">
        <button type="button" class="language-button" :class="{ open: isOpen }" @click="toggle">
            <i class="fa-solid fa-language"></i>
        </button>

        <!-- the view head clips its content, so the menu is rendered outside of it -->
        <Teleport to="body">
            <ul v-if="isOpen" ref="menu" class="language-menu" :style="menuStyle">
                <li
                    v-for="option in options"
                    :key="option.id"
                    class="language-option"
                    :class="{ selected: option.id === language.current }"
                    @click="select(option.id)"
                >
                    <img v-if="option.flag" :src="option.flag" class="flag" alt="" />
                    <span class="label">{{ option.label }}</span>
                </li>
            </ul>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
    import { Language } from "@/data/model/Enums";
    import { getLanguageString, languageSelectList } from "@/shared/localization/language";
    import { onBeforeUnmount, onMounted, ref } from "vue";

    import { useLanguageStore } from "@/app/stores/language";

    interface ILanguageOption {
        id: Language;
        label: string;
        flag?: string;
    }

    // inline flags, so no image assets have to be deployed for them
    const flags: Record<string, string> = {
        en:
            "data:image/svg+xml;utf8," +
            "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'>" +
            "<rect width='60' height='30' fill='%23012169'/>" +
            "<path d='M0 0 60 30M60 0 0 30' stroke='%23fff' stroke-width='6'/>" +
            "<path d='M0 0 60 30M60 0 0 30' stroke='%23C8102E' stroke-width='3'/>" +
            "<path d='M30 0V30M0 15H60' stroke='%23fff' stroke-width='10'/>" +
            "<path d='M30 0V30M0 15H60' stroke='%23C8102E' stroke-width='6'/></svg>",
        de:
            "data:image/svg+xml;utf8," +
            "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 5 3'>" +
            "<rect width='5' height='3' fill='%23000'/>" +
            "<rect y='1' width='5' height='2' fill='%23DD0000'/>" +
            "<rect y='2' width='5' height='1' fill='%23FFCE00'/></svg>",
        fr:
            "data:image/svg+xml;utf8," +
            "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 2'>" +
            "<rect width='3' height='2' fill='%23fff'/>" +
            "<rect width='1' height='2' fill='%23002395'/>" +
            "<rect x='2' width='1' height='2' fill='%23ED2939'/></svg>",
        it:
            "data:image/svg+xml;utf8," +
            "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 2'>" +
            "<rect width='3' height='2' fill='%23fff'/>" +
            "<rect width='1' height='2' fill='%23009246'/>" +
            "<rect x='2' width='1' height='2' fill='%23CE2B37'/></svg>",
    };

    const language = useLanguageStore();

    const isOpen = ref(false);
    const root = ref<HTMLElement>();
    const menu = ref<HTMLElement>();
    const menuStyle = ref<Record<string, string>>({});

    const options: ILanguageOption[] = languageSelectList.map((l) => ({
        id: l.id,
        label: getLanguageString(l.id).toUpperCase(),
        flag: flags[getLanguageString(l.id)],
    }));

    function toggle() {
        if (!isOpen.value) {
            // the menu is teleported to the body, so it has to be placed at the button manually.
            // it is anchored to whichever edges the button is closest to, otherwise it would open
            // off-screen for a button in the bottom right corner
            const bounds = root.value!.getBoundingClientRect();
            const style: Record<string, string> = {};

            if (bounds.left + bounds.width / 2 > window.innerWidth / 2) {
                style.right = `${window.innerWidth - bounds.right}px`;
            } else {
                style.left = `${bounds.left}px`;
            }

            if (bounds.top + bounds.height / 2 > window.innerHeight / 2) {
                style.bottom = `${window.innerHeight - bounds.top + 6}px`;
            } else {
                style.top = `${bounds.bottom + 6}px`;
            }

            menuStyle.value = style;
        }

        isOpen.value = !isOpen.value;
    }

    function select(id: Language) {
        language.setLanguage(id);
        isOpen.value = false;
    }

    function onDocumentMouseDown(event: MouseEvent) {
        const target = event.target as Node;
        if (root.value?.contains(target) === true) return;
        if (menu.value?.contains(target) === true) return;

        isOpen.value = false;
    }

    onMounted(() => document.addEventListener("mousedown", onDocumentMouseDown));
    onBeforeUnmount(() => document.removeEventListener("mousedown", onDocumentMouseDown));
</script>

<style scoped lang="scss">
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    // sits in the view head, right next to the home / back button on the left
    .language-selector {
        position: absolute;
        top: 0;
        left: 68px;
        height: 52px;
        display: flex;
        align-items: center;
        z-index: 900;
        pointer-events: all;
    }

    // ---- Button ----
    .language-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        background-color: #fff;
        border: 1px solid #dcdcdc;
        border-radius: 12px;
        color: #333;
        cursor: pointer;
        transition: background-color 0.15s ease;

        > i {
            font-size: 22px;
            line-height: 1;
        }

        &:hover,
        &.open {
            background-color: #f5f5f5;
        }
    }

    // ---- Dropdown menu ----
    // teleported to the body, positioned under the button from script
    .language-menu {
        position: fixed;
        z-index: 1100;
        list-style: none;
        margin: 0;
        padding: 4px;
        min-width: 120px;
        background-color: #fff;
        border: 1px solid #dcdcdc;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }

    .language-option {
        display: flex;
        align-items: center;
        // spacing and flag are in em, so they follow the font size below on every screen
        gap: 0.6em;
        padding: 0.5em 0.625em;
        border-radius: 6px;
        color: #333;
        // same size as the service names on the start page (ServiceItem .lbl)
        font-size: 16px;
        cursor: pointer;

        > .flag {
            flex: 0 0 auto;
            width: 1.875em;
            height: 1.125em;
            object-fit: cover;
            border: 1px solid #e2e2e2;
            border-radius: 2px;
        }

        &:hover {
            background-color: #f5f5f5;
        }

        &.selected {
            background-color: #f0f0f0;
            font-weight: 600;
        }
    }

    // bigger options on the kiosk screen (the menu is teleported to the body,
    // the kiosk-mode class sits on the html element so it still matches)
    .kiosk-mode .language-menu {
        min-width: 160px * variables.$kioskScale;
        padding: 10px * variables.$kioskScale;
        border-radius: 8px * variables.$kioskScale;
        // the screen saver is the topmost element in kiosk mode, the menu has to clear it
        z-index: 10001;

        .language-option {
            // as large as the kiosk service names on the start page (ServiceItem .lbl);
            // the em-based spacing and flag scale with it
            font-size: clamp(26px, 3.3vw, 70px);
            border-radius: 6px * variables.$kioskScale;
        }
    }
</style>
